import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'

export default function ForgotPassword() {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError('')

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email.trim(),
      {
        redirectTo: `${window.location.origin}/login`,
      }
    )

    if (resetError) {
      setError(resetError.message || t('auth.error_generic'))
      setLoading(false)
      return
    }

    setSent(true)
    setLoading(false)
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brand}>
          <span style={styles.brandMerq}>Merq</span>
          <span style={styles.brandLog}>Log</span>
        </div>

        {sent ? (
          <>
            <div style={styles.confirmIcon}>📧</div>
            <h2 style={styles.confirmTitle}>{t('auth.reset_sent')}</h2>
            <p style={styles.confirmBody}>
              {t('auth.reset_sent_body', { email })}
            </p>
            <Link to="/login" style={{ ...styles.switchLink, display: 'block', textAlign: 'center', marginTop: '20px' }}>
              {t('auth.back_to_login')}
            </Link>
          </>
        ) : (
          <>
            <h2 style={styles.title}>{t('auth.reset_password')}</h2>
            <p style={styles.subtitle}>{t('auth.reset_instructions')}</p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <label style={styles.label}>
                <span style={styles.labelText}>{t('auth.email')}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  required
                  style={styles.input}
                />
              </label>

              {error && <div style={styles.error}>{error}</div>}

              <button type="submit" disabled={loading} style={{
                ...styles.submit,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}>
                {loading ? t('auth.sending') : t('auth.send_reset_link')}
              </button>

              <Link to="/login" style={styles.back}>
                ← {t('auth.back_to_login')}
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

const plum = '#1E1040'
const teal = '#0D9488'

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    background: 'linear-gradient(135deg, #F7F9FC 0%, #E6FFFA 100%)',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: '#fff',
    borderRadius: '20px',
    padding: '40px 32px',
    boxShadow: '0 10px 40px rgba(30, 16, 64, 0.08)',
    border: '1px solid rgba(30, 16, 64, 0.06)',
  },
  brand: {
    fontFamily: "'Fraunces', serif",
    fontSize: '36px',
    fontWeight: 900,
    letterSpacing: '-1px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  brandMerq: { color: plum },
  brandLog: { color: teal, fontStyle: 'italic' },
  title: {
    fontFamily: "'Fraunces', serif",
    fontSize: '22px',
    fontWeight: 800,
    color: plum,
    textAlign: 'center',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.5,
    margin: '0 0 28px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  labelText: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1.5px solid #E5E7EB',
    fontSize: '15px',
    color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#B91C1C',
    fontSize: '13px',
    padding: '10px 12px',
    borderRadius: '8px',
    fontWeight: 500,
  },
  submit: {
    marginTop: '6px',
    padding: '14px',
    background: teal,
    color: '#042f2e',
    border: 'none',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  back: {
    marginTop: '8px',
    textAlign: 'center',
    fontSize: '13px',
    color: teal,
    fontWeight: 600,
    textDecoration: 'none',
  },
  switchLink: {
    color: teal,
    fontWeight: 700,
    textDecoration: 'none',
  },
  confirmIcon: {
    fontSize: '48px',
    textAlign: 'center',
    margin: '4px 0 12px 0',
  },
  confirmTitle: {
    fontFamily: "'Fraunces', serif",
    fontSize: '22px',
    fontWeight: 800,
    color: plum,
    textAlign: 'center',
    margin: '0 0 12px 0',
    letterSpacing: '-0.5px',
  },
  confirmBody: {
    fontSize: '14px',
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 1.6,
    margin: 0,
  },
}
